import type { Injector } from '@angular/core'
import { inject } from '@angular/core'
import { patchState, signalStoreFeature, withFeature, withMethods, withState } from '@ngrx/signals'
import { Events, injectDispatch, on, withEventHandlers, withReducer } from '@ngrx/signals/events'
import { filter, map, switchMap, take, tap } from 'rxjs'

import { getUniqueCurrentISOTimestamp } from '@app/core/get-unique-current-iso-timestamp'
import { isDefined } from '@app/core/is-defined'
import { sendingEvents } from '@app/modules/messaging/components/store/sending.events'
import type { ConversationProfile } from '@app/modules/messaging/models/conversation-profile.model'
import type { Conversation } from '@app/modules/messaging/models/conversation.model'
import type { MessageForm } from '@app/modules/messaging/models/message-form.model'
import { ConversationUIService } from '@app/modules/messaging/services/conversation-ui.service'
import { MessageCacheService } from '@app/modules/messaging/services/message-cache.service'
import { PopupService } from '@app/modules/popup/popup.service'
import { recordingFeature } from '@app/modules/recording/recording.feature'
import { ModalService } from '@app/overlays/modal/modal.service'

import { voiceSendingEvents } from './voice-sending.events'
import { voiceUploadEvents } from './voice-upload.events'
import { voiceUploadFeature } from './voice-upload.feature'
import { conversationEvents } from '../../../components/store/conversation.events'
import { VoiceMessageCreatorMicAccessAllowComponent } from '../components/voice-message-creator-mic-access-allow/voice-message-creator-mic-access-allow.component'
import { VoiceMessageCreatorMicAccessRequiredComponent } from '../components/voice-message-creator-mic-access-required/voice-message-creator-mic-access-required.component'
import type { UnsentVoiceMessage } from '../models/voice-message.model'

interface State {
  voice: UnsentVoiceMessage | undefined
  voiceFlow: string | undefined
  _userClickCounter: number
}

const initialState: State = {
  voice: undefined,
  voiceFlow: undefined,
  _userClickCounter: 0,
}

export const voiceSendingFeature = () => {
  return signalStoreFeature(
    withState(initialState),
    withFeature(() => voiceUploadFeature()),
    withFeature(() => recordingFeature('voice_message')),
    withMethods(
      (
        store,
        dispatch = injectDispatch(voiceSendingEvents),
        conversationUIService = inject(ConversationUIService),
        popupService = inject(PopupService),
        modalService = inject(ModalService),
      ) => {
        const openVoiceCreator = (
          conversation: Conversation,
          injector: Injector,
          messageForm: MessageForm,
          voice?: UnsentVoiceMessage,
        ) => {
          patchState(store, state => ({ _userClickCounter: state._userClickCounter + 1 }))

          if (store._userClickCounter() >= 3) {
            const instance = modalService.create(VoiceMessageCreatorMicAccessRequiredComponent).instance
            instance.showCheckBrowser = true
            return
          }

          if (store.permissionState() === 'prompt' && !voice) {
            const popupDisplayRequest = popupService.getPopupRequest('voice-message-allow-mic-access')

            if (popupDisplayRequest) {
              popupService.askForShowing('voice-message-allow-mic-access', () => {
                const { instance, componentRef } = modalService.create(VoiceMessageCreatorMicAccessAllowComponent)

                instance.onConfirm(() => {
                  store.askForPermission()
                })

                return { instance, componentRef }
              })
            } else {
              store.askForPermission()
            }

            if (store.error()) {
              modalService.create(VoiceMessageCreatorMicAccessRequiredComponent)
            }

            return
          }

          if (voice) {
            patchState(store, { voice, voiceFlow: voice.flow })
          } else {
            patchState(store, { voiceFlow: getUniqueCurrentISOTimestamp() })
          }

          conversationUIService.openVoiceMessageCreatorModal(conversation, injector, messageForm)
          patchState(store, { _userClickCounter: 0 })
        }

        const removeVoice = () => dispatch.removeVoice()

        const removeCache = (profile: ConversationProfile) => {
          dispatch.removeCache({ profile })
        }

        const exitVoice = () => dispatch.exitVoice()

        return { openVoiceCreator, removeCache, removeVoice, exitVoice }
      },
    ),
    withReducer(
      on(voiceSendingEvents.restoreCache, ({ payload: { voice } }) => ({ voice })),
      on(conversationEvents.select, sendingEvents.sendMessageSuccess, voiceSendingEvents.exitVoice, () => initialState),
      on(voiceSendingEvents.removeVoice, () => ({ voice: undefined })),
    ),
    withEventHandlers((_store, events = inject(Events), messageCacheService = inject(MessageCacheService)) => ({
      restoreCachedMessage$: events.on(conversationEvents.select).pipe(
        map(({ payload }) => payload.profileBrowseId),
        filter(isDefined),
        switchMap(() => events.on(conversationEvents.loadSuccess).pipe(take(1))),
        map(({ payload }) => payload.response.conversation.profile),
        map(profile =>
          voiceSendingEvents.restoreCache({
            voice: messageCacheService.getCachedMessage(profile).voice,
          }),
        ),
      ),

      saveCachedMessage$: events.on(voiceUploadEvents.uploadComplete).pipe(
        map(({ payload }) => payload),
        filter(({ voice }) => voice?.flow === _store.voiceFlow()),
        tap(({ profile, voice }) => {
          patchState(_store, { voice })
          messageCacheService.saveCachedMessage(profile, { voice })
        }),
      ),

      removeCachedMessage$: events.on(voiceSendingEvents.removeCache).pipe(
        map(({ payload }) => payload),
        tap(({ profile }) => {
          messageCacheService.saveCachedMessage(profile, { voice: undefined })
        }),
      ),
    })),
  )
}
